import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import bookingService from '../services/booking.service';
import toast from 'react-hot-toast';

// Async thunks
export const createBooking = createAsyncThunk(
  'booking/createBooking',
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await bookingService.createBooking(bookingData);
      toast.success('Booking created successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to create booking';
      // Don't show toast here - axios interceptor already handles it
      // This prevents duplicate error messages
      return rejectWithValue(message);
    }
  }
);

export const getUserBookings = createAsyncThunk(
  'booking/getUserBookings',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      // axios interceptor already returns response.data, so response is the full API response object
      // { success: true, data: [...], pagination: {...} }
      const response = await bookingService.getUserBookings(page, limit, filters);
      return response; // Return full response object, not response.data
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch bookings';
      return rejectWithValue(message);
    }
  }
);

export const getBookingById = createAsyncThunk(
  'booking/getBookingById',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await bookingService.getBookingById(bookingId);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch booking';
      return rejectWithValue(message);
    }
  }
);

export const updateBookingStatus = createAsyncThunk(
  'booking/updateBookingStatus',
  async ({ bookingId, status }, { rejectWithValue }) => {
    try {
      const response = await bookingService.updateBookingStatus(bookingId, status);
      toast.success('Booking status updated successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to update booking status';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const assignTechnician = createAsyncThunk(
  'booking/assignTechnician',
  async ({ bookingId, technicianId }, { rejectWithValue }) => {
    try {
      const response = await bookingService.assignTechnician(bookingId, technicianId);
      toast.success('Technician assigned successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to assign technician';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const cancelBooking = createAsyncThunk(
  'booking/cancelBooking',
  async ({ bookingId, reason }, { rejectWithValue }) => {
    try {
      const response = await bookingService.cancelBooking(bookingId, reason);
      toast.success('Booking cancelled successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to cancel booking';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getBookingStats = createAsyncThunk(
  'booking/getBookingStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await bookingService.getBookingStats();
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch booking stats';
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  bookings: [],
  currentBooking: null,
  stats: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
  filters: {
    status: '',
    urgency: '',
    assignedTechnicianId: '',
    serviceId: '',
  },
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        urgency: '',
        assignedTechnicianId: '',
        serviceId: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Create booking
      .addCase(createBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings.unshift(action.payload.booking);
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get user bookings
      .addCase(getUserBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserBookings.fulfilled, (state, action) => {
        state.loading = false;
        
        // Handle both array response and object response
        if (Array.isArray(action.payload)) {
          state.bookings = action.payload;
          state.pagination = {
            page: 1,
            limit: 10,
            total: action.payload.length,
            totalPages: Math.ceil(action.payload.length / 10),
          };
        } else {
          // API response structure: { success: true, data: [...], pagination: {...} }
          const bookingsData = action.payload.data || action.payload.bookings || [];
          const paginationData = action.payload.pagination || {};
          
          state.bookings = bookingsData;
          
          // Ensure pagination object is complete
          // IMPORTANT: Use paginationData.total directly - don't fallback to bookingsData.length
          // because bookingsData.length is only the current page count, not the total count
          const total = paginationData.total !== undefined && paginationData.total !== null 
            ? paginationData.total 
            : 0;
          const limit = paginationData.limit || 10;
          const page = paginationData.page || 1;
          
          state.pagination = {
            page,
            limit,
            total,
            totalPages: total > 0 ? Math.ceil(total / limit) : 0,
          };
        }
      })
      .addCase(getUserBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get booking by ID
      .addCase(getBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.booking;
      })
      .addCase(getBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update booking status
      .addCase(updateBookingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBooking = action.payload.booking;
        const index = state.bookings.findIndex(booking => booking._id === updatedBooking._id);
        if (index !== -1) {
          state.bookings[index] = updatedBooking;
        }
        if (state.currentBooking && state.currentBooking._id === updatedBooking._id) {
          state.currentBooking = updatedBooking;
        }
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Assign technician
      .addCase(assignTechnician.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignTechnician.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBooking = action.payload.booking;
        const index = state.bookings.findIndex(booking => booking._id === updatedBooking._id);
        if (index !== -1) {
          state.bookings[index] = updatedBooking;
        }
        if (state.currentBooking && state.currentBooking._id === updatedBooking._id) {
          state.currentBooking = updatedBooking;
        }
      })
      .addCase(assignTechnician.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel booking
      .addCase(cancelBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBooking = action.payload.booking;
        const index = state.bookings.findIndex(booking => booking._id === updatedBooking._id);
        if (index !== -1) {
          state.bookings[index] = updatedBooking;
        }
        if (state.currentBooking && state.currentBooking._id === updatedBooking._id) {
          state.currentBooking = updatedBooking;
        }
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get booking stats
      .addCase(getBookingStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBookingStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
      })
      .addCase(getBookingStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentBooking, setFilters, clearFilters } = bookingSlice.actions;
export default bookingSlice.reducer;
